import * as d3 from 'd3';

import { renderNeedle } from './Needle';
import { renderBands } from './Bands';
import { renderTicks } from './Ticks';
import { renderLabels } from './Labels';
import { mapToGaugeFormat } from './map';
import { GaugeDataFormat } from './GaugeDataFormat';
import { GaugeOptions } from './GaugeOptions';
import { trigonoScale } from './TrigonoScale';
import { GaugeFormat } from './GaugeFormat';
import { renderTarget, renderTargetArrow } from './Target';

export class Gauge {
    private _height = 100;

    height(): number;
    height(value: number): this;
    height(value?: number): this | number {
        if (arguments.length) {
            this._height = value;
            return this;
        }
        return this._height;
    }

    private _width = 100;
    width(): number;
    width(value: number): this;
    width(value?: number): this | number {
        if (arguments.length) {
            this._width = value;
            return this;
        }
        return this._width;
    }
    private _ratio = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number): this | number {
        if (arguments.length) {
            this._ratio = value;
            return this;
        }
        return this._ratio;
    }

    private _data: GaugeDataFormat[] = [];
    data(): GaugeDataFormat[];
    data(value: GaugeDataFormat[]): this;
    data(value?: GaugeDataFormat[]): this | GaugeDataFormat[] {
        if (arguments.length) {
            this._data = value;
            return this;
        }
        return this._data;
    }

    private _options: GaugeOptions = {
        ticksCount: 5,
        labelsCount: 2,
        tickSize: 10,
        thickness: 0.2,
        range: [-2 * Math.PI / 3, 2 * Math.PI / 3],
        isLegendVisible: false,
        isTitleVisible: false,
        title: undefined
    };
    options(): GaugeOptions;
    options(value: GaugeOptions): this;
    options(value?: GaugeOptions): this | GaugeOptions {
        if (arguments.length) {
            this._options = value;
            return this;
        }
        return this._options;
    }

    render(selection: d3.Selection<any>) {
        const options = this._options;
        const data = this._data;
        const width = this._width / data.length;
        const height = this._height / (1 + Math.abs(Math.sin(trigonoScale(options.range[0]))));
        const halfGaugeSize = Math.min(height, width / 2);
        const toBind = mapToGaugeFormat(data, options, halfGaugeSize, (this._ratio || 1));
        const gauge = selection
            .selectAll('.new-gauge')
            .data(toBind);

        // exit
        gauge.exit()
            .remove();

        // enter
        gauge.enter()
            .append('g')
            .classed('new-gauge', true)
            .call(this.enter);

        //update
        gauge.attr('transform', (d, i) => `translate(${(i + 1 / 2) * width},${halfGaugeSize})`);
        gauge.select('.bands')
            .call(renderBands);
        gauge.select('.labels')
            .call(renderLabels);
        gauge.select('.ticks')
            .call(renderTicks);
        gauge.select('.needle')
            .call(renderNeedle);
        if (options.showTarget) {
            gauge.select('.target')
                .call(renderTarget);
            gauge.select('.targetArrow')
                .call(renderTargetArrow);
        }

        gauge.select('.central-label')
            .select('text')
            .text(d => d.formattedValue)
            .classed('fixed-size', true)
            .transition()
            .style('font-size', d => d.fontSize + 'px');
    }

    private enter(enterGauge: d3.selection.Enter<GaugeFormat>) {
        enterGauge.append('g')
            .classed('bands', true);
        enterGauge.append('g')
            .classed('labels', true);
        enterGauge.append('g')
            .classed('needle', true);
        enterGauge.append('g')
            .classed('ticks', true);
        enterGauge.append('g')
            .classed('target', true);
        enterGauge.append('g')
            .classed('targetArrow', true);

        // label
        const enterCentralLabel = enterGauge.append('g')
            .classed('central-label', true);
        enterCentralLabel.append('g')
            .classed('main-value', true)
            .append('text');
        enterCentralLabel
            .append('g')
            .classed('label', true)
            .append('text');
    }
}